/**
 * Dataset export, and the SYNC half of "is this task finished?".
 *
 * "Reconciling" is the moment a task stops being work-in-progress: every one of
 * its segments has either been verified `correct` or been given a human
 * correction. At that point the task flips to `completed` and shows up on the
 * Done tab.
 *
 * THIS MODULE DOES NOT DO THE RECONCILING. The write is `db.reconcileTask`, and
 * that is what all five real call sites use. This file used to advertise a
 * `reconcileAfterChange` wrapper as the thing to call; nothing called it, and
 * it has been deleted rather than left as an instruction nobody follows.
 *
 * What is here:
 *  - the *rules* live in `src/types.ts` (`isSegmentResolved`, `finalTextOf`,
 *    `finalKindOf`); this file re-exposes them under the names the Done tab
 *    uses and adds the SYNC variants pages need, so a list of already-loaded
 *    segments does not have to hit IndexedDB again,
 *  - the export/download plumbing for the Done tab.
 *
 * A FILTERING DISCREPANCY YOU MUST KNOW ABOUT
 * -------------------------------------------
 * `isTaskResolved`, `areSegmentsResolved` and `toDatasetSegments` read the FULL
 * row set, while `db.getTaskProgress` and `db.areAllSegmentsResolved` read
 * ACTIVE LEAVES ONLY. On a task that has been recut, the superseded rows are
 * therefore counted here and not there — which means a recut task can export
 * OVERLAPPING spans in the dataset JSON. That is a real bug; it is NOT fixed in
 * this pass because changing it changes what "resolved" means and what Export
 * JSON emits. Tracked as follow-up F3.
 */

import { getSegmentsByTask, getTask, listTasksByStatus, reconcileAllTasks } from '../db';
import { round3 } from '../lib/num';
import {
  finalKindOf,
  finalTextOf,
  isSegmentResolved,
  type ExportPayload,
  type Segment,
  type SegmentKind,
  type Task,
} from '../types';

/* ------------------------------------------------------------------ */
/* Per-segment rules                                                   */
/* ------------------------------------------------------------------ */

/**
 * Final label for a segment: the human correction when there is one, otherwise
 * what the model said it heard.
 *
 * Note this deliberately uses `heard` (verbatim phonetics) and NOT `contextual`
 * for both branches — the dataset is a *heard-word* dataset; `contextual` is
 * only ever a hint shown to the human. Same rule as `finalTextOf` in types.ts,
 * re-exported here under the name the Done tab uses.
 */
export function finalWord(segment: Segment): string {
  return finalTextOf(segment);
}

/** Final kind for a segment (human `correctedKind` wins over the model's). */
export function finalKind(segment: Segment): SegmentKind {
  return finalKindOf(segment);
}

/** True when the human overrode the model (text and/or kind). */
export function wasCorrected(segment: Segment): boolean {
  return segment.correctedText !== undefined || segment.correctedKind !== undefined;
}

/* ------------------------------------------------------------------ */
/* Per-task rules                                                      */
/* ------------------------------------------------------------------ */

/**
 * Sync form of the resolution check, for callers that already hold the
 * segments. A task with zero segments is NOT resolved (nothing was labelled).
 */
export function areSegmentsResolved(segments: Segment[]): boolean {
  if (segments.length === 0) return false;
  return segments.every(isSegmentResolved);
}

/**
 * Is every segment of this task either verified-correct or corrected?
 * Reads from IndexedDB. Returns false for an unknown or empty task.
 */
export async function isTaskResolved(taskId: string): Promise<boolean> {
  const segments = await getSegmentsByTask(taskId);
  return areSegmentsResolved(segments);
}

/*
 * DELETED here, all three with zero callers:
 *  - `isSegmentDone`, a one-line alias of `types.isSegmentResolved`;
 *  - `finalizeTask`, superseded by `db.reconcileTask` (which also walks a
 *    completed task BACK to `review` when a segment is re-opened — this one
 *    only ever moved forwards);
 *  - `reconcileAfterChange`, which this file's own header used to instruct
 *    callers to use while all five of them called `db.reconcileTask` instead.
 */

/**
 * Completed tasks for the Done tab, newest first. Runs a reconcile sweep first
 * so tasks finished elsewhere in the session (or in a previous one that crashed
 * before the flip) surface here without a manual refresh.
 */
export async function listDoneTasks(): Promise<Task[]> {
  await reconcileAllTasks();
  return listTasksByStatus('completed');
}

/* ------------------------------------------------------------------ */
/* Transcript                                                          */
/* ------------------------------------------------------------------ */

/**
 * Human-readable transcript: final labels of the spoken segments joined with
 * spaces. Noise is dropped by default; fillers and unclear segments are kept
 * because they're part of what was actually said.
 */
export function transcriptOf(segments: Segment[], opts: { includeNoise?: boolean } = {}): string {
  return segments
    .filter((s) => (opts.includeNoise ? true : finalKind(s) !== 'noise'))
    .map((s) => finalWord(s).trim())
    .filter(Boolean)
    .join(' ');
}

/* ------------------------------------------------------------------ */
/* Dataset export                                                      */
/* ------------------------------------------------------------------ */

/** One row of the exported dataset. */
export interface DatasetSegment {
  /** Clip start, seconds from the beginning of the original audio. */
  start: number;
  /** Clip end, seconds. */
  end: number;
  /** Final label — human correction if there was one, else what was heard. */
  label: string;
  /** Final kind — `correctedKind` if there was one, else the model's. */
  kind: SegmentKind;
  /** Model confidence 0..1 as originally reported (never rewritten). */
  confidence: number;
  /**
   * Where `label` came from:
   *  - `model` : the model's `heard` text, which a human then accepted as-is
   *  - `human` : a human typed it or picked it from the "Other" sheet
   *
   * Every row of a completed task has been through a human either way, so a
   * plain `verified: true` flag would carry no information — this says which
   * kind of human judgement produced the label.
   */
  source: 'model' | 'human';
}

/** Shape written by the Done tab's "Export JSON" button. */
export interface DatasetPayload {
  task: Task;
  exportedAt: number;
  /** Convenience copy of the joined final labels. */
  transcript: string;
  segments: DatasetSegment[];
}

/** Build the dataset rows from already-loaded segments (sync). */
export function toDatasetSegments(segments: Segment[]): DatasetSegment[] {
  return segments.map((s) => ({
    start: round3(s.startSec),
    end: round3(s.endSec),
    label: finalWord(s),
    kind: finalKind(s),
    confidence: s.confidence,
    source: wasCorrected(s) ? 'human' : 'model',
  }));
}

/** Build the full payload from already-loaded data (sync). */
export function buildDatasetFrom(task: Task, segments: Segment[]): DatasetPayload {
  return {
    task,
    exportedAt: Date.now(),
    transcript: transcriptOf(segments),
    segments: toDatasetSegments(segments),
  };
}

/** Build the dataset payload for a task, reading from IndexedDB. */
export async function buildDataset(taskId: string): Promise<DatasetPayload | undefined> {
  const task = await getTask(taskId);
  if (!task) return undefined;
  const segments = await getSegmentsByTask(taskId);
  return buildDatasetFrom(task, segments);
}

/* ---- lossless dump: no UI entry point yet ------------------------- */

/**
 * The contract shape from `types.ts`: every stored field of every segment plus
 * the resolved `finalText`/`finalKind`.
 *
 * KEPT DELIBERATELY, despite having no button behind it. It is the documented
 * counterpart to {@link DatasetPayload} and the only irreversible item on the
 * cull list — deleting it would silently decide that the app never wants a raw
 * dump, which is not a decision a refactor gets to make.
 *
 * {@link DatasetPayload} is the trimmed, human-readable file the Done tab
 * downloads; this is the lossless dump for anyone who needs the raw records
 * (model `heard` vs `contextual`, verify state, the original ids). Both are
 * built from the same helpers so they can never disagree about a final label.
 */
export function toExportPayload(task: Task, segments: Segment[]): ExportPayload {
  return {
    task,
    exportedAt: Date.now(),
    segments: segments.map((s) => ({ ...s, finalText: finalTextOf(s), finalKind: finalKindOf(s) })),
  };
}

/** {@link toExportPayload}, reading from IndexedDB. */
export async function buildExportPayload(taskId: string): Promise<ExportPayload | undefined> {
  const task = await getTask(taskId);
  if (!task) return undefined;
  return toExportPayload(task, await getSegmentsByTask(taskId));
}

/** `my-recording.wav` -> `my-recording.labels.json` (filesystem-safe). */
export function datasetFileName(task: Task): string {
  const base = task.name.replace(/\.[a-z0-9]+$/i, '').replace(/[^\w.-]+/g, '-') || 'task';
  return `${base}.labels.json`;
}

/**
 * Trigger a browser download of a JSON payload. No-op outside a DOM.
 * Kept here (not in the page) so any tab can offer an export.
 */
export function downloadJson(payload: unknown, fileName: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // give Safari a tick to start the download before the URL dies
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Build + download a task's dataset in one call.
 * Returns false when the task no longer exists.
 */
export async function exportTaskDataset(taskId: string): Promise<boolean> {
  const payload = await buildDataset(taskId);
  if (!payload) return false;
  downloadJson(payload, datasetFileName(payload.task));
  return true;
}
